import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LanguageIcon from "@mui/icons-material/Language";

import Langs from "../../../app/lang/langs";
import { vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * V-Learning — a lesson that points at an external resource.
 *
 * The hostname is shown as prominently as the title. A learner is about to leave the platform
 * for a site somebody else controls, and being told where before clicking is the difference
 * between a link and a surprise.
 */

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

export default function LinkLesson({ lesson }) {

    const url = lesson && lesson.external_url ? String(lesson.external_url) : ''
    const hostname = readHostname(url)

    // A URL whose scheme is not http(s) is not rendered as a link at all. `javascript:` in an
    // href executes in this page's origin the moment it is clicked, and this value comes from
    // an author's keyboard.
    if (!hostname) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_LINK_MISSING" />
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ ...vlearnSurface.panel, maxWidth: 620 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                    width: 46, height: 46, borderRadius: '12px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(0, 103, 184, 0.10)', color: '#0067b8'
                }} aria-hidden="true">
                    <LanguageIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...vlearnType.sectionTitle, wordBreak: 'break-word' }}>
                        {hostname}
                    </Typography>
                    <Typography sx={{ ...vlearnType.meta, mt: 0.5, wordBreak: 'break-all' }}>
                        {url}
                    </Typography>
                </Box>
            </Box>

            <Typography sx={{ ...vlearnType.body, mt: 2 }}>
                <Langs str="VLEARN_LINK_EXTERNAL_NOTE" />
            </Typography>

            <Button
                variant="contained"
                size="small"
                component="a"
                href={url}
                target="_blank"
                // `noopener` severs the `window.opener` reference the new tab would otherwise
                // hold on this one, which is what stops an external page from navigating the
                // platform tab out from under the learner. `noreferrer` keeps the platform's
                // own URL out of the destination's logs.
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{
                    ...focusRing, mt: 2, textTransform: 'none', fontSize: '0.82rem',
                    backgroundColor: 'var(--orange, rgb(255,144,21))',
                    '&:hover': { backgroundColor: '#e07f0d' }
                }}
            >
                <Langs str="VLEARN_OPEN_LINK" />
            </Button>
        </Box>
    )
}

/** The host of an http(s) URL, or null for anything this lesson should not link to. */
function readHostname(url) {
    if (!url) return null
    try {
        const parsed = new URL(url)
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
        return parsed.hostname
    } catch (error) {
        return null
    }
}
